import React, { useState, useEffect, useRef } from "react";

type Props = {};

// Helper function for random colors (for brick variety)
function getRandomColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export default function BreakerGamePage({}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameState, setGameState] = useState<
    "playing" | "paused" | "gameOver" | "won"
  >("playing"); // Game state
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">(
    "medium"
  ); // Difficulty level
  const [paddleSpeed, setPaddleSpeed] = useState(10); // Store paddle speed
  const [brickRowCount, setBrickRowCount] = useState(5);
  const [brickColumnCount, setBrickColumnCount] = useState(7);

  // Game Objects (using interfaces for better type safety)
  interface Ball {
    x: number;
    y: number;
    radius: number;
    dx: number; // Velocity in x direction
    dy: number; // Velocity in y direction
    color: string;
  }

  interface Paddle {
    x: number;
    width: number;
    height: number;
    color: string;
  }

  interface Brick {
    x: number;
    y: number;
    width: number;
    height: number;
    status: number; // 1 for visible, 0 for broken
    color: string;
  }

  const [ball, setBall] = useState<Ball>({
    x: 0, // Initialize in the useEffect
    y: 0,
    radius: 10,
    dx: 2,
    dy: -2,
    color: "#FFFFFF", // White ball
  });

  const [paddle, setPaddle] = useState<Paddle>({
    x: 0, // Initialize in the useEffect
    width: 75,
    height: 10,
    color: "#0095DD", // Paddle color
  });

  const [bricks, setBricks] = useState<Brick[]>([]); // Array of bricks

  const [rightPressed, setRightPressed] = useState(false);
  const [leftPressed, setLeftPressed] = useState(false);

  // Initialization (runs once when component mounts)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return; // Important: Check if canvas is available

    const context = canvas.getContext("2d");
    if (!context) return; // Check if 2D context is available

    canvas.width = window.innerWidth * 0.8; // Responsive width (80% of viewport)
    canvas.height = window.innerHeight * 0.7; // Responsive height (70% of viewport)
    // Set initial paddle and ball positions *after* canvas dimensions are set
    const initialPaddleX = (canvas.width - paddle.width) / 2;
    setPaddle((prevPaddle) => ({ ...prevPaddle, x: initialPaddleX }));
    setBall((prevBall) => ({
      ...prevBall,
      x: canvas.width / 2,
      y: canvas.height - 30,
    }));

    initBricks();

    const handleResize = () => {
      if (!canvasRef.current) return;
      canvasRef.current.width = window.innerWidth * 0.8;
      canvasRef.current.height = window.innerHeight * 0.7;
      // Recalculate paddle position on resize.  Very important!
      const newPaddleX = (canvasRef.current.width - paddle.width) / 2;
      setPaddle((prev) => ({ ...prev, x: newPaddleX }));

      //Recalculate the ball position
      setBall((prevBall) => ({
        ...prevBall,
        x: canvasRef.current!.width / 2,
        y: canvasRef.current!.height - 30,
      }));

      initBricks();
    };

    window.addEventListener("resize", handleResize);

    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === "Right" || e.key === "ArrowRight") {
        setRightPressed(true);
      } else if (e.key === "Left" || e.key === "ArrowLeft") {
        setLeftPressed(true);
      } else if (e.key === "Escape" || e.key === "p") {
        if (gameState === "playing") {
          setGameState("paused");
        } else if (gameState === "paused") {
          setGameState("playing");
        }
      }
    };

    const keyUpHandler = (e: KeyboardEvent) => {
      if (e.key === "Right" || e.key === "ArrowRight") {
        setRightPressed(false);
      } else if (e.key === "Left" || e.key === "ArrowLeft") {
        setLeftPressed(false);
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    document.addEventListener("keyup", keyUpHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      document.removeEventListener("keyup", keyUpHandler);
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    //Game Loop
    if (gameState !== "playing") return; // Only run if playing

    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const intervalId = setInterval(() => {
      context.clearRect(0, 0, canvas.width, canvas.height); // Clear canvas
      drawBricks(context);
      drawBall(context);
      drawPaddle(context);
      drawScore(context);
      drawLives(context);
      collisionDetection();

      let paddleX = paddle.x;
      if (rightPressed) {
        paddleX += paddleSpeed; // Use paddleSpeed
        if (paddleX + paddle.width > canvas.width) {
          paddleX = canvas.width - paddle.width;
        }
      } else if (leftPressed) {
        paddleX -= paddleSpeed; // Use paddleSpeed
        if (paddleX < 0) {
          paddleX = 0;
        }
      }
      setPaddle((prevPaddle) => ({ ...prevPaddle, x: paddleX }));

      // Ball movement
      let newBallX = ball.x + ball.dx;
      let newBallY = ball.y + ball.dy;

      // Bounce off walls
      if (newBallX + ball.radius > canvas.width || newBallX - ball.radius < 0) {
        setBall((prevBall) => ({ ...prevBall, dx: -prevBall.dx }));
      }
      if (newBallY - ball.radius < 0) {
        setBall((prevBall) => ({ ...prevBall, dy: -prevBall.dy }));
      } else if (newBallY + ball.radius > canvas.height) {
        // Check for paddle hit
        if (newBallX > paddle.x && newBallX < paddle.x + paddle.width) {
          setBall((prevBall) => ({ ...prevBall, dy: -prevBall.dy })); //Bounce
        } else {
          // Ball missed paddle
          setLives((prevLives) => prevLives - 1);
          if (lives - 1 <= 0) {
            setGameState("gameOver");
          } else {
            //Reset
            setBall((prevBall) => ({
              ...prevBall,
              x: canvas.width / 2,
              y: canvas.height - 30,
              dx: 2 * (difficulty === "hard" ? 1.5 : 1), // Adjust speed based on difficulty
              dy: -2 * (difficulty === "hard" ? 1.5 : 1), // Adjust speed based on difficulty
            }));
            setPaddle((prevPaddle) => ({
              ...prevPaddle,
              x: (canvas.width - paddle.width) / 2,
            }));
          }
        }
      }

      setBall((prevBall) => ({
        ...prevBall,
        x: prevBall.x + prevBall.dx,
        y: prevBall.y + prevBall.dy,
      }));
    }, 10); //  ~60 frames per second

    return () => clearInterval(intervalId);
  }, [
    gameState,
    ball,
    paddle,
    bricks,
    rightPressed,
    leftPressed,
    score,
    lives,
    difficulty,
    paddleSpeed,
  ]);

  const initBricks = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const brickWidth = (canvas.width * 0.8) / brickColumnCount; //Responsive
    const brickHeight = canvas.height * 0.05; //Responsive

    const newBricks = [];
    for (let c = 0; c < brickColumnCount; c++) {
      for (let r = 0; r < brickRowCount; r++) {
        const brickX =
          c * (brickWidth + canvas.width * 0.02) + canvas.width * 0.1; // Add padding and offset
        const brickY =
          r * (brickHeight + canvas.height * 0.02) + canvas.height * 0.08; // Add padding and offset
        newBricks.push({
          x: brickX,
          y: brickY,
          width: brickWidth,
          height: brickHeight,
          status: 1,
          color: getRandomColor(),
        });
      }
    }
    setBricks(newBricks);
  };

  const drawBricks = (context: CanvasRenderingContext2D) => {
    bricks.forEach((brick) => {
      if (brick.status === 1) {
        context.beginPath();
        context.rect(brick.x, brick.y, brick.width, brick.height);
        context.fillStyle = brick.color;
        context.fill();
        context.closePath();
      }
    });
  };

  const drawBall = (context: CanvasRenderingContext2D) => {
    context.beginPath();
    context.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
    context.fillStyle = ball.color;
    context.fill();
    context.closePath();
  };

  const drawPaddle = (context: CanvasRenderingContext2D) => {
    context.beginPath();
    context.rect(
      paddle.x,
      canvasRef.current!.height - paddle.height,
      paddle.width,
      paddle.height
    );
    context.fillStyle = paddle.color;
    context.fill();
    context.closePath();
  };

  const drawScore = (context: CanvasRenderingContext2D) => {
    context.font = "16px Arial";
    context.fillStyle = "#FFFFFF";
    context.fillText("Score: " + score, 8, 20); // Position at top-left
  };
  const drawLives = (context: CanvasRenderingContext2D) => {
    context.font = "16px Arial";
    context.fillStyle = "#FFFFFF";
    context.fillText("Lives: " + lives, canvasRef.current!.width - 65, 20); // Top-right
  };

  const collisionDetection = () => {
    const updatedBricks = [...bricks]; // Create a copy
    let brickHit = false;

    for (let i = 0; i < updatedBricks.length; i++) {
      const brick = updatedBricks[i];
      if (brick.status === 1) {
        if (
          ball.x > brick.x &&
          ball.x < brick.x + brick.width &&
          ball.y > brick.y &&
          ball.y < brick.y + brick.height
        ) {
          setBall((prevBall) => ({ ...prevBall, dy: -prevBall.dy }));
          brick.status = 0; // Mark as broken
          brickHit = true;
          setScore((prevScore) => prevScore + 10); //Update

          // Check for win condition
          if (updatedBricks.every((b) => b.status === 0)) {
            setGameState("won");
          }
        }
      }
    }
    if (brickHit) {
      setBricks(updatedBricks); //Update
    }
  };

  const handleDifficultyChange = (
    newDifficulty: "easy" | "medium" | "hard"
  ) => {
    setDifficulty(newDifficulty);

    //Adjust speeds and brick layout based on difficulty
    switch (newDifficulty) {
      case "easy":
        setPaddleSpeed(12);
        setBall((prevBall) => ({ ...prevBall, dx: 2, dy: -2 }));
        setBrickRowCount(3);
        setBrickColumnCount(5);
        break;
      case "medium":
        setPaddleSpeed(10);
        setBall((prevBall) => ({ ...prevBall, dx: 2, dy: -2 }));
        setBrickRowCount(5);
        setBrickColumnCount(7);
        break;
      case "hard":
        setPaddleSpeed(8);
        setBall((prevBall) => ({ ...prevBall, dx: 3, dy: -3 }));
        setBrickRowCount(7);
        setBrickColumnCount(9);
        break;
    }
    // Re-initialize bricks whenever difficulty changes
    initBricks(); //  Important: Call initBricks after changing rows/cols
  };

  const resetGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setScore(0);
    setLives(3);
    setGameState("playing");
    setBall({
      x: canvas.width / 2,
      y: canvas.height - 30,
      radius: 10,
      dx: 2 * (difficulty === "hard" ? 1.5 : 1),
      dy: -2 * (difficulty === "hard" ? 1.5 : 1),
      color: "#FFFFFF",
    });
    setPaddle({
      x: (canvas.width - 75) / 2,
      width: 75,
      height: 10,
      color: "#0095DD",
    });

    initBricks(); // Reset bricks
  };

  return (
    <div
      style={{
        backgroundColor: "#282c34", // Dark background
        color: "white",
        minHeight: "100vh", // Full viewport height
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center", // Center content vertically
        padding: "20px",
        boxSizing: "border-box", // Include padding in element's total width/height
      }}
    >
      <h1
        style={{
          marginBottom: "20px",
          fontFamily: "'Press Start 2P', cursive",
          fontSize: "2rem",
        }}
      >
        <span style={{ color: "#0095DD" }}>B</span>reaker{" "}
        <span style={{ color: "#0095DD" }}>G</span>ame
      </h1>

      <div style={{ marginBottom: "20px" }}>
        <label style={{ marginRight: "10px" }}>Difficulty:</label>
        <select
          value={difficulty}
          onChange={(e) =>
            handleDifficultyChange(e.target.value as "easy" | "medium" | "hard")
          }
          style={{
            padding: "8px",
            borderRadius: "5px",
            backgroundColor: "#444",
            color: "white",
            border: "1px solid #666",
            marginRight: "10px",
          }}
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
        <button
          onClick={resetGame}
          style={{
            padding: "8px 16px",
            borderRadius: "5px",
            backgroundColor: "#0095DD",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontFamily: "'Press Start 2P', cursive",
          }}
        >
          Retry
        </button>
      </div>

      <canvas
        ref={canvasRef}
        style={{
          border: "1px solid white", // Keep a visible border
          borderRadius: "10px", // Rounded corners for modern look
          backgroundColor: "#333", // Slightly lighter background for the canvas
          boxShadow: "0px 0px 15px rgba(255, 255, 255, 0.3)", // Subtle glow effect
          maxWidth: "100%", // Ensure responsiveness
          maxHeight: "70vh", // Limit height
        }}
      />

      {gameState === "gameOver" && (
        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
            fontSize: "1.5rem",
            fontFamily: "'Press Start 2P', cursive",
          }}
        >
          <p style={{ color: "#DD0000" }}>Game Over!</p>
          <p>Final Score: {score}</p>
          <button
            onClick={resetGame}
            style={{
              padding: "10px 20px",
              borderRadius: "5px",
              backgroundColor: "#0095DD",
              color: "white",
              border: "none",
              cursor: "pointer",
              marginTop: "15px",
              fontFamily: "'Press Start 2P', cursive",
            }}
          >
            Play Again
          </button>
        </div>
      )}

      {gameState === "won" && (
        <div
          style={{ marginTop: "20px", textAlign: "center", fontSize: "1.5rem" }}
        >
          <p style={{ color: "#00DD00" }}>You Won!</p>
          <p>Final Score: {score}</p>
          <button
            onClick={resetGame}
            style={{
              padding: "10px 20px",
              borderRadius: "5px",
              backgroundColor: "#0095DD",
              color: "white",
              border: "none",
              cursor: "pointer",
              marginTop: "15px",
              fontFamily: "'Press Start 2P', cursive",
            }}
          >
            Play Again
          </button>
        </div>
      )}
      {gameState === "paused" && (
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <p
            style={{
              fontSize: "1.5rem",
              fontFamily: "'Press Start 2P', cursive",
              color: "yellow",
            }}
          >
            Paused
          </p>
          <p
            style={{
              fontSize: "1rem",
              fontFamily: "'Press Start 2P', cursive",
              color: "white",
            }}
          >
            Press 'P' or 'ESC' to resume
          </p>
        </div>
      )}
      <link
        href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap"
        rel="stylesheet"
      />
    </div>
  );
}
